import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { useToast } from "../hooks/use-toast";
import { format } from "date-fns";
import { MapPin, Star, Calendar as CalendarIcon, Users, ArrowRight, Sparkles, Loader2, Check, Copy, ChevronLeft, ChevronRight, X, Info } from "lucide-react";
import HotelDetails from "./HotelDetails";

const PublicHotels = () => {
  const { toast } = useToast();
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cityFilter, setCityFilter] = useState("all");
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.listHotels();
        setHotels(data);
      } catch (e) {
        toast({ title: "Could not load hotels", description: e.message });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cities = ["all", ...Array.from(new Set(hotels.map((h) => h.city))).sort()];
  const filtered = cityFilter === "all" ? hotels : hotels.filter((h) => h.city === cityFilter);

  return (
    <section id="hotels" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div className="max-w-2xl">
            <span className="text-[11px] tracking-[0.25em] font-medium text-[#c8102e] uppercase">Book Direct</span>
            <h2 className="text-3xl md:text-5xl font-serif font-medium text-neutral-900 mt-3 leading-tight">
              Our hotels, ready to welcome you
            </h2>
            <p className="text-neutral-600 mt-3">Browse our latest properties across cities and towns. Reserve in seconds — pay in cash at the hotel.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {cities.slice(0, 8).map((c) => (
              <button
                key={c}
                onClick={() => setCityFilter(c)}
                className={`text-xs uppercase tracking-widest px-3 py-2 border transition-colors ${cityFilter === c ? "border-[#c8102e] bg-[#c8102e] text-white" : "border-neutral-200 text-neutral-700 hover:border-neutral-400"}`}
              >
                {c === "all" ? "All Cities" : c}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-neutral-500"><Loader2 className="animate-spin mr-2 text-[#c8102e]" size={20} /> Loading properties…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24 text-neutral-500 border border-dashed border-neutral-300">No hotels match your filter yet.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((h) => (
              <HotelCard key={h.id} hotel={h} onBook={() => setSelectedHotel(h)} />
            ))}
          </div>
        )}
      </div>

      <BookingDialog
        hotel={selectedHotel}
        onClose={() => setSelectedHotel(null)}
        onConfirmed={(b) => { setSelectedHotel(null); setConfirmed(b); }}
      />
      <ConfirmationDialog booking={confirmed} onClose={() => setConfirmed(null)} />
    </section>
  );
};

const HotelCard = ({ hotel, onBook, onDetails }) => {
  const gallery = (hotel.images && hotel.images.length ? hotel.images : (hotel.image ? [hotel.image] : []));
  const [idx, setIdx] = useState(0);
  const has = gallery.length > 0;
  const many = gallery.length > 1;
  const go = (delta) => setIdx((i) => (i + delta + gallery.length) % gallery.length);
  return (
    <div className="group bg-white border border-neutral-200 hover:border-[#c8102e] transition-colors flex flex-col">
      <div className="aspect-[16/10] overflow-hidden relative">
        {has ? (
          <>
            {gallery.map((src, i) => (
              <img
                key={i}
                src={src}
                alt={`${hotel.name} ${i + 1}`}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${i === idx ? "opacity-100" : "opacity-0"} ${i === idx ? "group-hover:scale-105" : ""} transition-transform duration-700`}
              />
            ))}
            {many && (
              <>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); go(-1); }}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/85 hover:bg-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Previous"
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); go(1); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/85 hover:bg-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Next"
                >
                  <ChevronRight size={16} />
                </button>
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1">
                  {gallery.map((_, i) => (
                    <button
                      key={i}
                      onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                      className={`h-1.5 rounded-full transition-all ${i === idx ? "w-5 bg-white" : "w-1.5 bg-white/60 hover:bg-white/90"}`}
                      aria-label={`Slide ${i + 1}`}
                    />
                  ))}
                </div>
                <div className="absolute top-3 right-3 bg-black/60 text-white text-[10px] uppercase tracking-widest px-2 py-1">
                  {idx + 1} / {gallery.length}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="w-full h-full bg-neutral-100 flex items-center justify-center text-neutral-400 text-xs">No image</div>
        )}
        {hotel.featured && (
          <Badge className="absolute top-3 left-3 bg-[#c8102e] text-white rounded-none">
            <Sparkles size={11} className="mr-1" /> Featured
          </Badge>
        )}
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <div className="text-[10px] uppercase tracking-widest text-[#c8102e]">{hotel.brand}</div>
        <h3 className="font-serif text-xl font-medium mt-1 leading-tight">{hotel.name}</h3>
        <div className="flex items-center gap-2 text-xs text-neutral-500 mt-1">
          <MapPin size={12} /> {hotel.city}, {hotel.country}
          <span className="flex items-center gap-0.5 ml-auto">
            {Array.from({ length: hotel.star_rating || 0 }).map((_, i) => <Star key={i} size={12} className="fill-amber-400 text-amber-400" />)}
          </span>
        </div>
        {hotel.description && <p className="text-sm text-neutral-600 mt-3 line-clamp-2 flex-1">{hotel.description}</p>}

        <div className="mt-4 flex flex-wrap gap-1">
          {(hotel.room_types || []).slice(0, 3).map((rt) => (
            <span key={rt} className="text-[10px] uppercase tracking-widest border border-neutral-200 px-1.5 py-0.5 text-neutral-700">{rt}</span>
          ))}
        </div>

        <div className="mt-5 flex items-end justify-between border-t border-neutral-100 pt-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">From</div>
            <div className="text-lg font-serif font-medium">{hotel.currency} {Number(hotel.price_per_night).toLocaleString("en-IN")}<span className="text-xs text-neutral-500 font-sans font-normal"> / night</span></div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onDetails} className="rounded-none border-neutral-900 text-neutral-900 hover:bg-neutral-900 hover:text-white">
              Details
            </Button>
            <Button onClick={onBook} className="bg-[#c8102e] hover:bg-[#a70d26] text-white rounded-none">
              Book <ArrowRight size={14} className="ml-1" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const BookingDialog = ({ hotel, onClose, onConfirmed }) => {
  const { toast } = useToast();
  const today = new Date();
  const tmr = new Date(); tmr.setDate(today.getDate() + 1);
  const [checkIn, setCheckIn] = useState(today);
  const [checkOut, setCheckOut] = useState(tmr);
  const [roomType, setRoomType] = useState("");
  const [rooms, setRooms] = useState(1);
  const [guests, setGuests] = useState(2);
  const [firstName, setFirst] = useState("");
  const [lastName, setLast] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (hotel) {
      setRoomType(hotel.__preferredRoom || hotel.room_types?.[0] || "");
      setCheckIn(new Date());
      const t = new Date(); t.setDate(t.getDate() + 1);
      setCheckOut(t);
      setRooms(1); setGuests(2);
      setFirst(""); setLast(""); setPhone(""); setEmail(""); setNotes("");
    }
  }, [hotel]);

  if (!hotel) return null;

  const nights = Math.max(1, Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24)));
  const total = Number(hotel.price_per_night) * nights * rooms;

  const submit = async (e) => {
    e.preventDefault();
    if (!firstName || !lastName || !phone || !roomType) {
      toast({ title: "Missing details", description: "Please fill your name, phone and room type." });
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        hotel_id: hotel.id,
        guest_first_name: firstName,
        guest_last_name: lastName,
        phone,
        email: email || undefined,
        check_in: format(checkIn, "yyyy-MM-dd"),
        check_out: format(checkOut, "yyyy-MM-dd"),
        room_type: roomType,
        rooms: parseInt(rooms) || 1,
        guests: parseInt(guests) || 1,
        payment_method: "cash",
        payment_status: "pending",
        source: "web",
        notes,
      };
      const b = await api.createBooking(payload);
      onConfirmed(b);
    } catch (err) {
      toast({ title: "Booking failed", description: err.response?.data?.detail || err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!hotel} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Book · {hotel.name}</DialogTitle>
          <p className="text-xs text-neutral-500 mt-1 flex items-center gap-1"><MapPin size={12} /> {hotel.city}, {hotel.country} · {hotel.brand}</p>
        </DialogHeader>

        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <div>
            <Label>Check-in *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" type="button" className="w-full justify-start font-normal rounded-none">
                  <CalendarIcon size={14} className="mr-2" /> {format(checkIn, "EEE, d MMM yyyy")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={checkIn} onSelect={(d) => d && setCheckIn(d)} initialFocus /></PopoverContent>
            </Popover>
          </div>
          <div>
            <Label>Check-out *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" type="button" className="w-full justify-start font-normal rounded-none">
                  <CalendarIcon size={14} className="mr-2" /> {format(checkOut, "EEE, d MMM yyyy")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={checkOut} onSelect={(d) => d && setCheckOut(d)} initialFocus /></PopoverContent>
            </Popover>
          </div>

          <div>
            <Label>Room type *</Label>
            <Select value={roomType} onValueChange={setRoomType}>
              <SelectTrigger><SelectValue placeholder="Select room" /></SelectTrigger>
              <SelectContent>
                {(hotel.room_types || []).map((rt) => <SelectItem key={rt} value={rt}>{rt}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Rooms</Label>
              <Input type="number" min="1" value={rooms} onChange={(e) => setRooms(e.target.value)} />
            </div>
            <div>
              <Label>Guests</Label>
              <Input type="number" min="1" value={guests} onChange={(e) => setGuests(e.target.value)} />
            </div>
          </div>

          <div>
            <Label>First name *</Label>
            <Input value={firstName} onChange={(e) => setFirst(e.target.value)} />
          </div>
          <div>
            <Label>Last name *</Label>
            <Input value={lastName} onChange={(e) => setLast(e.target.value)} />
          </div>
          <div>
            <Label>Phone *</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98706 27398" />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
          </div>

          <div className="md:col-span-2">
            <Label>Special requests</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Airport pickup, early check-in, dietary preferences..." />
          </div>

          <div className="md:col-span-2 bg-neutral-50 border border-neutral-200 p-4 flex items-center justify-between">
            <div className="text-sm">
              <div className="text-neutral-600">
                <Users size={12} className="inline mr-1" /> {guests} guest{guests > 1 ? "s" : ""} · {rooms} room{rooms > 1 ? "s" : ""} · {nights} night{nights > 1 ? "s" : ""}
              </div>
              <div className="text-xs text-neutral-500 mt-1">Pay at hotel in cash on arrival. No card required.</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Total</div>
              <div className="text-2xl font-serif font-medium">{hotel.currency} {total.toLocaleString("en-IN")}</div>
            </div>
          </div>

          <div className="md:col-span-2 flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-none">Cancel</Button>
            <Button type="submit" disabled={submitting} className="bg-[#c8102e] hover:bg-[#a70d26] text-white rounded-none px-6">
              {submitting ? "Confirming..." : "Confirm booking"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ConfirmationDialog = ({ booking, onClose }) => {
  const { toast } = useToast();
  if (!booking) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(booking.booking_id);
      toast({ title: "Copied", description: "Booking ID copied to clipboard." });
    } catch (_) {}
  };
  return (
    <Dialog open={!!booking} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <div className="text-center py-4">
          <div className="w-14 h-14 mx-auto bg-emerald-50 text-emerald-700 flex items-center justify-center mb-4">
            <Check size={22} />
          </div>
          <span className="text-[11px] tracking-[0.25em] font-medium text-emerald-700 uppercase">Booking Confirmed</span>
          <h3 className="font-serif text-2xl font-medium mt-2">See you at {booking.hotel_name}</h3>
          <p className="text-sm text-neutral-600 mt-2">Please pay <span className="font-medium">{booking.currency} {Number(booking.amount).toLocaleString("en-IN")}</span> in cash at the hotel on arrival.</p>

          <div className="mt-4 inline-flex items-center gap-2 bg-[#c8102e]/5 border border-[#c8102e]/30 text-[#c8102e] px-3 py-2 text-xs uppercase tracking-widest">
            <Sparkles size={14} /> +10 AIP Credits added to your wallet
          </div>

          <div className="mt-6 border border-neutral-200 bg-neutral-50 p-4 text-left">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Booking ID</div>
            <div className="flex items-center justify-between mt-1">
              <span className="font-mono text-lg font-medium">{booking.booking_id}</span>
              <button onClick={copy} className="p-2 hover:bg-white border border-neutral-200"><Copy size={14} /></button>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
              <div><span className="text-neutral-500">Check-in:</span> <span className="font-medium">{booking.check_in}</span></div>
              <div><span className="text-neutral-500">Check-out:</span> <span className="font-medium">{booking.check_out}</span></div>
              <div><span className="text-neutral-500">Guest:</span> <span className="font-medium">{booking.guest_first_name} {booking.guest_last_name}</span></div>
              <div><span className="text-neutral-500">Room:</span> <span className="font-medium">{booking.room_type}</span></div>
            </div>
          </div>

          <div className="mt-6 flex gap-2 justify-center">
            <Button variant="outline" onClick={onClose} className="rounded-none">Close</Button>
            <a href={`/my-booking`} className="inline-flex items-center bg-[#c8102e] hover:bg-[#a70d26] text-white px-5 py-2 text-sm">
              View my booking
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PublicHotels;
